import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../api/useApi";
import { fetchCredits, redeemCode, startCheckout, type CreditsView } from "../api/resources";

export type UseCreditAnswer = "ok" | "no-credit" | "error";

/** A pack's price in its own currency, as a UK buyer reads it (£14.99). */
export function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP" }).format(price);
  } catch {
    return `${price} ${currency}`;
  }
}

/** How many times the page re-reads the balance after Stripe sends the buyer back, before saying it is late. */
const WEBHOOK_WAIT_READS = 15;

/**
 * "Get the premium analysis of this swing" (Growth Phase 0, eleetz-paid-tier.plan.md), on the free result of a user who
 * is not an admin. With a credit: spend one on this upload (onUseCredit → upgradeUploadToPro). Without: the packs on
 * sale → Stripe Checkout, which returns here with ?credits=bought; the webhook issues the credits a moment later, so
 * the balance is re-read until they arrive.
 */
/** How long a pack's credits last, as a buyer reads it: 365 days is "12 months". */
export function validFor(days: number): string {
  return days === 365 ? "12 months" : `${days} days`;
}

/** The validity line under the packs: one sentence when they all last alike, else the exceptions named (the season pack
 * of 20 lasts a year while the others last 30 days; owner, 2026-10-22). */
export function validityNote(packs: { title: string; validityDays: number }[]): string {
  const counts = new Map<number, number>();
  for (const p of packs) counts.set(p.validityDays, (counts.get(p.validityDays) ?? 0) + 1);
  const usual = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 30;
  const others = packs.filter((p) => p.validityDays !== usual).map((p) => `${validFor(p.validityDays)} for ${p.title}`);
  return `Credits are valid for ${validFor(usual)} from purchase${others.length ? ` (${others.join(", ")})` : ""}.`;
}

export default function PremiumPaywall({
  uploadId,
  search,
  onUseCredit,
  go = (url: string) => window.location.assign(url),
  pollMs = 2000,
}: {
  uploadId: number;
  search: string;
  onUseCredit: () => Promise<UseCreditAnswer>;
  go?: (url: string) => void;
  pollMs?: number;
}) {
  const apiFetch = useApi();
  // The balance is read on mount (and while waiting for the webhook), not again whenever the api function's identity
  // changes: re-reading on it made a render loop wherever useApi is not memoised.
  const apiRef = useRef(apiFetch);
  apiRef.current = apiFetch;
  const [credits, setCredits] = useState<CreditsView | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const backFromStripe = new URLSearchParams(search).get("credits") === "bought";
  const [waiting, setWaiting] = useState(backFromStripe);
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState("");

  // An API without /billing/credits (or an error body) reads as no credits and nothing on sale, never a broken page.
  const load = useCallback(
    () =>
      fetchCredits(apiRef.current).then((raw) => {
        const c: CreditsView = {
          available: Number(raw?.available) || 0,
          nextExpiryUtc: raw?.nextExpiryUtc ?? null,
          packs: Array.isArray(raw?.packs) ? raw.packs : [],
        };
        setCredits(c);
        return c;
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    let reads = 0;
    const tick = () => {
      load().then(
        (c) => {
          if (cancelled) return;
          reads++;
          if (backFromStripe && c.available === 0 && reads < WEBHOOK_WAIT_READS) setTimeout(tick, pollMs);
          else setWaiting(false);
        },
        () => { if (!cancelled) setWaiting(false); }
      );
    };
    tick();
    return () => { cancelled = true; };
  }, [load, backFromStripe, pollMs]);

  const buy = async (packKey: string) => {
    setBusy(true);
    setNote(null);
    try {
      const { checkoutUrl } = await startCheckout(apiFetch, packKey, uploadId);
      go(checkoutUrl);
    } catch {
      setNote("Could not open the payment page. Please try again.");
      setBusy(false);
    }
  };

  const use = async () => {
    setBusy(true);
    setNote(null);
    const answer = await onUseCredit();
    setBusy(false);
    if (answer === "no-credit") void load();
    if (answer === "error") setNote("Could not start the premium analysis. Your credit was not used.");
  };

  // Growth Phase 4: a coach's code (or a gift code) becomes a credit to spend here.
  const redeem = async () => {
    setBusy(true);
    setNote(null);
    try {
      await redeemCode(apiFetch, code);
      setCode("");
      setCodeOpen(false);
      await load();
    } catch {
      setNote("That code is not valid or has already been used.");
    } finally {
      setBusy(false);
    }
  };

  if (!credits) return null;
  const onSale = credits.packs.filter((p) => p.forSale);

  return (
    <div className="mt-3 rounded-xl border border-el-200 bg-el-50 p-4" data-testid="premium-paywall">
      <div className="text-sm font-semibold text-slate-800">Get the premium analysis of this swing</div>
      <p className="mt-1 text-xs text-slate-600">
        Club tracking and a narrated coaching video of your swing. One credit per analysis.
      </p>
      {waiting && credits.available === 0 && (
        <p className="mt-2 text-xs text-slate-600">Payment received. Your credits arrive in a moment…</p>
      )}
      {credits.available > 0 ? (
        <button type="button" className="btn btn-primary mt-3 text-xs" disabled={busy} onClick={() => void use()}>
          {`Use 1 credit (${credits.available} left)`}
        </button>
      ) : onSale.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">Premium can't be bought right now. Please try again later.</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {onSale.map((p) => (
            <button key={p.packKey} type="button" className="btn btn-secondary text-xs" disabled={busy} onClick={() => void buy(p.packKey)}>
              {`${p.title} · ${formatPrice(p.price, p.currency)}`}
            </button>
          ))}
        </div>
      )}
      {credits.available === 0 && onSale.length > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">{`${validityNote(onSale)} Paid securely with Stripe.`}</p>
      )}
      {credits.available === 0 && (codeOpen ? (
        <div className="mt-3 flex items-center gap-2">
          <input
            aria-label="Code"
            className="rounded-md border border-slate-300 px-2 py-1 text-xs uppercase"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="COACH-XXXXXX"
          />
          <button type="button" className="btn btn-secondary text-xs" disabled={busy || code.trim() === ""} onClick={() => void redeem()}>
            Redeem
          </button>
        </div>
      ) : (
        <button type="button" className="mt-2 text-xs text-el-700 underline" onClick={() => setCodeOpen(true)}>
          Have a code?
        </button>
      ))}
      {note && <p className="mt-2 text-xs text-rose-600">{note}</p>}
    </div>
  );
}
